import { ChangeEvent, Fragment, useMemo, useRef, useState } from 'react'
import { GridColDef } from '@mui/x-data-grid'
import { enqueueSnackbar } from 'notistack'
import { useAPI } from '../../API'
import { Alert, Button, Grid, Loader, Modal, Table, Typography } from '../../components'
import { getOrderStatusLabel } from '../../utils'
import { parseShipFile, ShipFileRow } from '../../utils/parseShipFile'

type OrderRow = {
    id: number
    transaction: string
    status: string
    ship_date?: string | null
    buyer?: {
        name?: string
        email?: string
    }
}

type PreviewRow = ShipFileRow & {
    id: number
    orderId?: number
    buyer_name?: string
    current_status?: string
    current_ship_date?: string | null
    canConvert: boolean
    reason?: string
}

type BulkShipModalProps = {
    open: boolean
    onClose: () => void
    orders: OrderRow[]
    onSuccess: () => void
}

export function BulkShipModal({ open, onClose, orders, onSuccess }: BulkShipModalProps) {
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [parsing, setParsing] = useState(false)
    const [previewRows, setPreviewRows] = useState<PreviewRow[]>([])
    const [fileName, setFileName] = useState('')

    const [bulkShipState, bulkShip] = useAPI({
        url: '/order/bulk-ship',
        method: 'POST',
    }, {
        manual: true,
    })

    const ordersByTransaction = useMemo(() => {
        return new Map(orders.map(order => [order.transaction.toUpperCase(), order]))
    }, [orders])

    const convertibleCount = useMemo(
        () => previewRows.filter(row => row.canConvert).length,
        [previewRows]
    )

    const notFoundCount = useMemo(
        () => previewRows.filter(row => !row.canConvert).length,
        [previewRows]
    )

    const columns: GridColDef[] = useMemo(() => [
        { field: 'transaction', headerName: 'HP', flex: 1 },
        { field: 'ship_date', headerName: 'Nova Data de Envio', flex: 1 },
        { field: 'buyer_name', headerName: 'Comprador', flex: 1, valueGetter: ({ row }) => row.buyer_name || '-' },
        {
            field: 'current_status',
            headerName: 'Situação Atual',
            flex: 1.2,
            valueGetter: ({ row }) => row.current_status ? getOrderStatusLabel(row.current_status) : '-',
        },
        {
            field: 'reason',
            headerName: 'Resultado',
            flex: 1,
            valueGetter: ({ row }) => row.canConvert ? 'Será atualizado' : (row.reason || 'Ignorado'),
        },
    ], [])

    const resetState = () => {
        setPreviewRows([])
        setFileName('')
        setParsing(false)
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    const handleClose = () => {
        if (bulkShipState.loading) {
            return
        }

        resetState()
        onClose()
    }

    const buildPreview = (rows: ShipFileRow[]): PreviewRow[] => {
        return rows.map((row, index) => {
            const order = ordersByTransaction.get(row.transaction)

            if (!order) {
                return {
                    ...row,
                    id: index + 1,
                    canConvert: false,
                    reason: 'Pedido não encontrado',
                }
            }

            if (order.status === 'CANCELED') {
                return {
                    ...row,
                    id: index + 1,
                    orderId: order.id,
                    buyer_name: order.buyer?.name,
                    current_status: order.status,
                    current_ship_date: order.ship_date,
                    canConvert: false,
                    reason: 'Pedido cancelado',
                }
            }

            return {
                ...row,
                id: index + 1,
                orderId: order.id,
                buyer_name: order.buyer?.name,
                current_status: order.status,
                current_ship_date: order.ship_date,
                canConvert: true,
            }
        })
    }

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]

        if (!file) {
            return
        }

        setParsing(true)

        try {
            const rows = await parseShipFile(file)
            setFileName(file.name)
            setPreviewRows(buildPreview(rows))
        } catch (error) {
            resetState()
            enqueueSnackbar(error instanceof Error ? error.message : 'Erro ao ler a planilha.', {
                variant: 'error',
            })
        } finally {
            setParsing(false)
        }
    }

    const handleConfirm = async () => {
        const items = previewRows
            .filter(row => row.canConvert)
            .map(({ transaction, ship_date }) => ({ transaction, ship_date }))

        if (items.length === 0) {
            enqueueSnackbar('Nenhuma linha válida para converter.', { variant: 'error' })
            return
        }

        try {
            const { status, data } = await bulkShip({
                data: { items },
            })

            if (status <= 299) {
                const updated = data?.updated ?? items.length
                const failedCount = data?.failed?.length ?? 0

                enqueueSnackbar(
                    failedCount > 0
                        ? `${updated} pedidos atualizados. ${failedCount} falharam.`
                        : `${updated} pedidos atualizados com sucesso!`,
                    { variant: failedCount > 0 ? 'warning' : 'success' }
                )

                resetState()
                onSuccess()
                onClose()
            }
        } catch {
            enqueueSnackbar('Erro ao converter pedidos.', { variant: 'error' })
        }
    }

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Importar Envios"
            onConfirm={previewRows.length > 0 && convertibleCount > 0 && !bulkShipState.loading ? handleConfirm : undefined}
            confirmLabel={`Converter ${convertibleCount} linhas`}
        >
            <Grid container spacing={2} minWidth="70vw" maxWidth="90vw">
                <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                        Envie um arquivo CSV ou XLS com as colunas <strong>HP</strong> e <strong>DATA ENVIO</strong>.
                        Os pedidos encontrados terão a situação alterada para Enviado e a data de envio preenchida.
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                    <Button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={parsing || bulkShipState.loading}
                    >
                        {fileName ? 'Trocar arquivo' : 'Selecionar arquivo'}
                    </Button>
                    {fileName ? (
                        <Typography variant="body2" component="span" sx={{ marginLeft: 2 }}>
                            {fileName}
                        </Typography>
                    ) : null}
                </Grid>

                {(parsing || bulkShipState.loading) && (
                    <Grid item xs={12} display="flex" justifyContent="center" padding={4}>
                        <Loader />
                    </Grid>
                )}

                {!parsing && !bulkShipState.loading && previewRows.length > 0 ? (
                    <Fragment>
                        <Grid item xs={12}>
                            <Alert severity={convertibleCount > 0 ? 'info' : 'warning'}>
                                {previewRows.length} linha(s) na planilha.
                                {' '}
                                {convertibleCount} serão convertidas
                                {notFoundCount > 0 ? ` e ${notFoundCount} serão ignoradas.` : '.'}
                            </Alert>
                        </Grid>
                        <Grid item xs={12}>
                            <div style={{ height: 420, width: '100%' }}>
                                <Table
                                    columns={columns}
                                    rows={previewRows}
                                    getRowId={(row) => row.id}
                                    density="compact"
                                    pageSizeOptions={[25, 50, 100]}
                                    initialState={{
                                        pagination: { paginationModel: { pageSize: 25 } },
                                    }}
                                    getRowClassName={({ row }) => row.canConvert ? '' : 'bulk-ship-ignored'}
                                    sx={{
                                        '& .bulk-ship-ignored': {
                                            opacity: 0.65,
                                        },
                                    }}
                                />
                            </div>
                        </Grid>
                    </Fragment>
                ) : null}
            </Grid>
        </Modal>
    )
}
